import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, Grid, TextFieldProps } from '@mui/material';

import { getCurriculumV2State } from '../../../../../store/V2/CurriculumSlice';
import { useAppSelector } from '../../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../components/Inputs/UserListDropdown';
import {
  COURSE_STATUS_OPTIONS,
  TCourseStatus,
} from '../../../../../store/V2/CoursesSlice';

type FilterData = {
  limit: number;
  offset: number;
  curriculum?: string;
  subject?: string;
  grade?: string;
  studentId?: string;
  tutorId?: string;
  status?: TCourseStatus;
};

interface FilterSectionProps {
  updateFilterState: (attributes: Partial<FilterData>) => void;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedStudent: TUserSelectOption | null;
  selectedTutor: TUserSelectOption | null;
  status: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialFilterData = {
  limit: 30,
  offset: 0,
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
  selectedStudent: null,
  selectedTutor: null,
  status: null,
};

const FilterSection: React.FC<FilterSectionProps> = ({ updateFilterState }) => {
  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);

  const [state, setState] = useState<TState>({ ...initialState });

  const [curriculumOptions, setCurriculumOptions] = useState<TSelectOption[]>(
    []
  );
  const [subjectOptions, setSubjectOptions] = useState<TSelectOption[]>([]);
  const [gradeOptions, setGradeOptions] = useState<TSelectOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<TSelectOption[]>([]);

  useEffect(() => {
    setStatusOptions([
      {
        label: 'Published',
        value: COURSE_STATUS_OPTIONS.ACTIVE,
      },
      {
        label: 'UnPublished',
        value: COURSE_STATUS_OPTIONS.DRAFT,
      },
    ]);
  }, []);

  useEffect(() => {
    if (curriculumState.list) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );
    }
  }, [curriculumState.list]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    }));

    updateFilterState({
      curriculum: value ? value.value : undefined,
      subject: undefined,
      grade: undefined,
      ...initialFilterData,
    });

    const newSubjectOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedGrade: null,
    }));

    updateFilterState({
      subject: value ? value.value : undefined,
      grade: undefined,
      ...initialFilterData,
    });

    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length &&
        value
      ) {
        const selectedSubjectData = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value.value
        );

        if (
          selectedSubjectData &&
          selectedSubjectData.grade &&
          selectedSubjectData.grade.length
        ) {
          selectedSubjectData.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));

    updateFilterState({
      grade: value ? value.value : undefined,
      ...initialFilterData,
    });
  };

  const handleStudentChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TUserSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedStudent: value,
    }));

    updateFilterState({
      studentId: value ? value.value : undefined,
      ...initialFilterData,
    });
  };

  const handleTutorChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TUserSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedTutor: value,
    }));

    updateFilterState({
      tutorId: value ? value.value : undefined,
      ...initialFilterData,
    });
  };

  const handlePublishChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      status: value,
    }));

    updateFilterState({
      status: value ? (value.value as TCourseStatus) : undefined,
      ...initialFilterData,
    });
  };

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedCurriculum}
              options={curriculumOptions}
              onChange={handleCurriculumChange}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Curriculum"
                  placeholder="Select"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedSubject}
              options={subjectOptions}
              onChange={handleSubjectChange}
              disabled={!state.selectedCurriculum}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Subject"
                  placeholder="Select"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedGrade}
              options={gradeOptions}
              onChange={handleGradeChange}
              disabled={!state.selectedSubject}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Grade"
                  placeholder="Select"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <UserListDropdown
              selectedItem={state.selectedStudent}
              handleChange={handleStudentChange}
              textFieldProps={{
                label: 'Student',
                placeholder: 'Select',
                ...textFieldProps,
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <UserListDropdown
              selectedItem={state.selectedTutor}
              handleChange={handleTutorChange}
              textFieldProps={{
                label: 'Tutor',
                placeholder: 'Select',
                ...textFieldProps,
              }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.status}
              options={statusOptions}
              onChange={handlePublishChange}
              popupIcon={TPAutoCompleteArrowIconComponent}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Status"
                  placeholder="Select"
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );
};

export default FilterSection;
